import React, { useState, useEffect } from "react";
import { COLORS } from "../../theme";

export interface ContrastSliderProps {
    minPercent: number;
    maxPercent: number;
    onMinChange: (value: number) => void;
    onMaxChange: (value: number) => void;
    onReset?: () => void;
    disabled?: boolean;
}

export function ContrastSlider({
    minPercent,
    maxPercent,
    onMinChange,
    onMaxChange,
    onReset,
    disabled = false
}: ContrastSliderProps) {
    const [localMin, setLocalMin] = useState(minPercent);
    const [localMax, setLocalMax] = useState(maxPercent);

    useEffect(() => {
        setLocalMin(minPercent);
        setLocalMax(maxPercent);
    }, [minPercent, maxPercent]);

    const handleMinChange = (value: number) => {
        const newMin = Math.min(value, localMax - 5); // Ensure min doesn't exceed max - 5%
        setLocalMin(newMin);
        onMinChange(newMin); // Always trigger the callback
    };

    const handleMaxChange = (value: number) => {
        const newMax = Math.max(value, localMin + 5); // Ensure max doesn't go below min + 5%
        setLocalMax(newMax);
        onMaxChange(newMax); // Always trigger the callback
    };

    const handleTrackClick = (event: React.MouseEvent) => {
        if (disabled) return;

        const rect = event.currentTarget.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const clickPercent = (clickX / rect.width) * 100;

        // Determine which slider to move based on click position
        const midPoint = (localMin + localMax) / 2;

        if (clickPercent < midPoint) {
            // Click is closer to min slider
            handleMinChange(clickPercent);
        } else {
            // Click is closer to max slider
            handleMaxChange(clickPercent);
        }
    };

    const handleMouseDown = (type: 'min' | 'max') => {
        // Enable dragging for the specific slider
        if (type === 'min') {
            document.addEventListener('mousemove', handleMinMouseMove);
            document.addEventListener('mouseup', handleMinMouseUp);
        } else {
            document.addEventListener('mousemove', handleMaxMouseMove);
            document.addEventListener('mouseup', handleMaxMouseUp);
        }
    };

    const handleMinMouseMove = (event: MouseEvent) => {
        const track = document.querySelector('[data-track]') as HTMLElement;
        if (!track) return;

        const rect = track.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const clickPercent = Math.max(0, Math.min(100, (clickX / rect.width) * 100));

        handleMinChange(clickPercent);
    };

    const handleMaxMouseMove = (event: MouseEvent) => {
        const track = document.querySelector('[data-track]') as HTMLElement;
        if (!track) return;

        const rect = track.getBoundingClientRect();
        const clickX = event.clientX - rect.left;
        const clickPercent = Math.max(0, Math.min(100, (clickX / rect.width) * 100));

        handleMaxChange(clickPercent);
    };

    const handleMinMouseUp = () => {
        document.removeEventListener('mousemove', handleMinMouseMove);
        document.removeEventListener('mouseup', handleMinMouseUp);
    };

    const handleMaxMouseUp = () => {
        document.removeEventListener('mousemove', handleMaxMouseMove);
        document.removeEventListener('mouseup', handleMaxMouseUp);
    };

    return (
        <div style={{
            color: '#fff',
            fontFamily: 'Arial, sans-serif',
            fontSize: 13,
            minWidth: 280,
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'left',
                marginBottom: 8,
                fontSize: 12,
                fontWeight: 500,
                color: '#ccc'
            }}>
                <span>Contrast</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* <span style={{ fontSize: 11, color: '#999' }}>
                        {localMin.toFixed(0)}% - {localMax.toFixed(0)}%
                    </span> */}
                    {onReset && (
                        <button
                            onClick={onReset}
                            disabled={disabled}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#999',
                                fontSize: 10,
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                padding: '2px 6px',
                                borderRadius: 3,
                                opacity: disabled ? 0.5 : 1
                            }}
                            title="Reset to default"
                        >
                            ↺
                        </button>
                    )}
                </div>
            </div>

            <div style={{ position: 'relative', marginBottom: 8 }}>
                {/* Background track */}
                <div
                    data-track
                    style={{
                        height: 6,
                        background: COLORS.background,
                        borderRadius: 3,
                        position: 'relative',
                        cursor: disabled ? 'not-allowed' : 'pointer'
                    }}
                    onClick={handleTrackClick}
                >
                    {/* Active range highlight */}
                    <div style={{
                        position: 'absolute',
                        left: `${localMin}%`,
                        width: `${localMax - localMin}%`,
                        height: '100%',
                        background: COLORS.focusBorder,
                        borderRadius: 3,
                        transition: 'all 0.1s ease'
                    }} />

                    {/* Min slider thumb */}
                    <div
                        style={{
                            position: 'absolute',
                            left: `${localMin}%`,
                            top: -6,
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            background: COLORS.focusBorder,
                            border: `2px solid ${COLORS.focusBorder}`,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            transform: 'translateX(-50%)',
                            transition: 'all 0.1s ease',
                            zIndex: 2
                        }}
                        onMouseDown={() => handleMouseDown('min')}
                    />

                    {/* Max slider thumb */}
                    <div
                        style={{
                            position: 'absolute',
                            left: `${localMax}%`,
                            top: -6,
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            background: COLORS.focusBorder,
                            border: `2px solid ${COLORS.focusBorder}`,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            transform: 'translateX(-50%)',
                            transition: 'all 0.1s ease',
                            zIndex: 2
                        }}
                        onMouseDown={() => handleMouseDown('max')}
                    />
                </div>

                {/* Min slider - for dragging only */}
                <input
                    type="range"
                    min={0}
                    max={100}
                    value={localMin}
                    onChange={(e) => handleMinChange(Number(e.target.value))}
                    disabled={disabled}
                    style={{
                        position: 'absolute',
                        top: -3,
                        left: 0,
                        width: '100%',
                        height: 12,
                        background: 'transparent',
                        pointerEvents: 'none',
                        WebkitAppearance: 'none',
                        appearance: 'none',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: 0,
                        zIndex: 1
                    }}
                />

                {/* Max slider - for dragging only */}
                <input
                    type="range"
                    min={0}
                    max={100}
                    value={localMax}
                    onChange={(e) => handleMaxChange(Number(e.target.value))}
                    disabled={disabled}
                    style={{
                        position: 'absolute',
                        top: -3,
                        left: 0,
                        width: '100%',
                        height: 12,
                        background: 'transparent',
                        pointerEvents: 'none',
                        WebkitAppearance: 'none',
                        appearance: 'none',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: 0,
                        zIndex: 1
                    }}
                />
            </div>

            {/* Min/Max labels */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 11,
                color: '#999'
            }}>
                <span>Min: {localMin.toFixed(0)}%</span>
                <span>Max: {localMax.toFixed(0)}%</span>
            </div>
        </div>
    );
} 